import { useCallback, useEffect } from 'react';
import { useEvent, useUnit } from 'effector-react';
import { sessionStorageExpandedMenuKey } from '@nx-frontend/features/menu-tree';
import { $expandedPortalMenuTypes, setExpandedMenuTypesEvent } from '@nx-frontend/shared/store/feature-menu-tree';
import { MenuType } from '@nx-frontend/shared/types';

export const useExpandPortalMenu = (menuType?: MenuType) => {
  const [expandedMenuTypes] = useUnit([$expandedPortalMenuTypes]);
  const [setExpandedMenuTypes] = useEvent([setExpandedMenuTypesEvent]);
  const isExpanded = menuType ? expandedMenuTypes.includes(menuType) : false;

  useEffect(() => {
    const stringValue = localStorage.getItem(sessionStorageExpandedMenuKey);
    if (stringValue) {
      const value = JSON.parse(stringValue);
      setExpandedMenuTypes(value);
    } else if (stringValue === null) {
      // If the value is null, it means that the user has never expanded the menu before,
      // in this case, open the portal sidebar by default
      setExpandedMenuTypes([MenuType.PORTAL_SIDEBAR]);
    }
  }, [setExpandedMenuTypes]);

  const toggle = useCallback(
    ({ menuType }: { menuType: MenuType }) => {
      let value: MenuType[];
      if (expandedMenuTypes.includes(menuType)) {
        value = expandedMenuTypes.filter((type) => type !== menuType);
      } else {
        value = [...expandedMenuTypes, menuType];
      }
      setExpandedMenuTypes(value);
      localStorage.setItem(sessionStorageExpandedMenuKey, JSON.stringify(value));
    },
    [expandedMenuTypes, setExpandedMenuTypes]
  );

  const set = useCallback(
    ({ menuType, value }: { menuType: MenuType; value: boolean }) => {
      const stringItems = localStorage.getItem(sessionStorageExpandedMenuKey);
      let items: MenuType[] = (stringItems ? JSON.parse(stringItems) : []) as MenuType[];
      if (value) {
        if (!items.includes(menuType)) {
          items.push(menuType);
        }
      } else {
        items = items.filter((type) => type !== menuType);
      }
      setExpandedMenuTypes(items);
      localStorage.setItem(sessionStorageExpandedMenuKey, JSON.stringify(items));
    },
    [setExpandedMenuTypes]
  );

  return {
    isMenuExpanded: isExpanded,
    toggleMenuExpand: toggle,
    setMenuExpanded: set,
  };
};
